import React, {useMemo, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, defineMessages} from 'react-intl'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration'
import {Flex, Text, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useBrand} from '../../../contexts/brand-context'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'

const LinkWithRegistration = withRegistration(Link)

const messages = defineMessages({
    earnPointsGarage: {
        id: 'loyalty.earnPoints.garage',
        defaultMessage:
            'Buy this product and earn <strong>{points} {points, plural, one {point} other {points}}</strong>'
    },
     earnPointsDynamite: {
        id: 'loyalty.earnPoints.dynamite',
        defaultMessage:
            'Buy this product and earn <strong>{points} {points, plural, one {point} other {points}}</strong>'
    },
    dontLetPointsGetAwayGarage: {
        id: 'loyalty.dontLetPointsGetAway.garage',
        defaultMessage:
            "Don't let <strong>{points} {points, plural, one {point} other {points}}</strong> get away!"
    },
    dontLetPointsGetAwayDynamite: {
        id: 'loyalty.dontLetPointsGetAway.dynamite',
        defaultMessage:
            "Don't let <strong>{points} {points, plural, one {point} other {points}}</strong> get away!"
    },
    joinCollectifGarage: {
        id: 'loyalty.joinCollectif.garage',
        defaultMessage: 'Join the collectif'
    },
    joinCollectifDynamite: {
        id: 'loyalty.joinCollectif.dynamite',
        defaultMessage: 'Join the collectif'
    }
})

const useCustomerLoyaltyState = () => {
    const {data: customer, isLoading, error} = useCurrentCustomer()

    return useMemo(
        () => ({
            isRegistered: customer?.isRegistered || false,
            isGuest: customer?.isGuest,
            hasDoublePoints: customer?.c_doublePointsEnabled || false,
            isLoading,
            hasCustomerData: customer !== undefined,
            error
        }),
        [customer?.isRegistered, customer?.c_doublePointsEnabled, isLoading, customer, error]
    )
}

const Loyalty = ({points}) => {
    const {isRegistered, isLoading, isGuest} = useCustomerLoyaltyState()
    const [hasResolved, setHasResolved] = useState(false)
    const {isGarage} = useBrand()
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()

    useEffect(() => {
        if (!isLoading || isGuest === true || isRegistered) {
            setHasResolved(true)
        }
    }, [isLoading, isGuest, isRegistered])

    if (!points) return null
    if (shouldHideLoyaltyUI && hasResolved) return null

    return (
        <Skeleton isLoaded={hasResolved}>
            <Flex
                p="3"
                bg="gray.200"
                color="loyaltyT3"
                fontSize="sm"
                justifyContent={isRegistered ? 'center' : 'space-between'}
            >
                <Text fontWeight="500">
                    {isGarage ? (
                        <FormattedMessage
                            {...(isRegistered
                                ? messages.earnPointsGarage
                                : messages.dontLetPointsGetAwayGarage)}
                            values={{
                                points,
                                strong: (chunks) => <strong>{chunks}</strong>
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            {...(isRegistered
                                ? messages.earnPointsDynamite
                                : messages.dontLetPointsGetAwayDynamite)}
                            values={{
                                points,
                                strong: (chunks) => <strong>{chunks}</strong>
                            }}
                        />
                    )}
                </Text>

                {!isRegistered && hasResolved && (
                    <LinkWithRegistration
                        fontWeight="500"
                        href="#"
                        textDecoration="underline"
                        data-testid="pdp-button-join-collectif"
                    >
                        {isGarage ? (
                            <FormattedMessage {...messages.joinCollectifGarage} />
                        ) : (
                            <FormattedMessage {...messages.joinCollectifDynamite} />
                        )}
                    </LinkWithRegistration>
                )}
            </Flex>
        </Skeleton>
    )
}

Loyalty.propTypes = {
    points: PropTypes.number
}

export default Loyalty
